import { api } from "./api";

export type WeeklyMenuStatus = "DRAFT" | "PUBLISHED" | "ARCHIVED";
export type MenuSlotType = "ENTREE" | "PLATS" | "LEGUMES_100" | "DESSERT" | "PREMIUM" | "PREMIUM_PLUS";

export interface MenuSlot {
  id: string;
  menuId: string;
  date: string;
  slotType: MenuSlotType;
  displayOrder: number;
  productId: string | null;
  priceOverride: number | string | null;
  estimatedPortions: number | null;
  isSwitch?: boolean;
  notes: string | null;
  product?: {
    id: string;
    name: string;
    price: number | string;
    isSpicy?: boolean;
    isVegetarian?: boolean;
    category?: { id: string; name: string };
  };
}

export interface WeeklyMenu {
  id: string;
  weekStartDate: string;
  weekEndDate: string;
  status: WeeklyMenuStatus;
  notes: string | null;
  slots: MenuSlot[];
}

export interface UpsertSlotInput {
  date: string;
  slotType: MenuSlotType;
  displayOrder?: number;
  productId?: string | null;
  priceOverride?: number | null;
  estimatedPortions?: number | null;
  isSwitch?: boolean;
  notes?: string | null;
}

export interface EstimatedQuantityBreakdown {
  productName: string;
  date: string;
  portions: number;
  quantityPerPortion: number;
  total: number;
}

export interface EstimatedIngredientQuantity {
  ingredientId: string;
  ingredientName: string;
  unit: string | null;
  totalQuantity: number;
  breakdown: EstimatedQuantityBreakdown[];
}

const base = "/weekly-menus";

export const weeklyMenuService = {
  async getAll(status?: WeeklyMenuStatus): Promise<WeeklyMenu[]> {
    const params = status ? { status } : {};
    const res = await api.get<{ success: boolean; data: WeeklyMenu[] }>(base, { params });
    return res.data?.data ?? [];
  },

  async getById(id: string): Promise<WeeklyMenu> {
    const res = await api.get<{ success: boolean; data: WeeklyMenu }>(`${base}/${id}`);
    return res.data!.data;
  },

  async getByWeek(weekStartDate: string): Promise<WeeklyMenu> {
    const res = await api.get<{ success: boolean; data?: WeeklyMenu }>(`${base}/by-week`, {
      params: { weekStartDate },
    });
    if (!res.data?.data) {
      throw new Error(`Menu de la semaine introuvable (${weekStartDate})`);
    }
    return res.data.data;
  },

  /** Récupère le menu de la semaine s'il existe, sans le créer (null sinon). */
  async getByWeekIfExists(weekStartDate: string): Promise<WeeklyMenu | null> {
    const res = await api.get<{ success: boolean; data?: WeeklyMenu }>(`${base}/by-week-exists`, {
      params: { weekStartDate },
      validateStatus: (status) => (status >= 200 && status < 300) || status === 404,
    });
    if (res.status === 404) return null;
    return res.data?.data ?? null;
  },

  async update(id: string, data: { status?: WeeklyMenuStatus; notes?: string | null }): Promise<WeeklyMenu> {
    const res = await api.put<{ success: boolean; data: WeeklyMenu }>(`${base}/${id}`, data);
    return res.data!.data;
  },

  async upsertSlot(menuId: string, input: UpsertSlotInput, slotId?: string): Promise<MenuSlot> {
    if (slotId) {
      const res = await api.put<{ success: boolean; data: MenuSlot }>(`${base}/${menuId}/slots/${slotId}`, input);
      return res.data!.data;
    }
    const res = await api.post<{ success: boolean; data: MenuSlot }>(`${base}/${menuId}/slots`, input);
    return res.data!.data;
  },

  async deleteSlot(menuId: string, slotId: string): Promise<void> {
    await api.delete(`${base}/${menuId}/slots/${slotId}`);
  },

  async duplicateWeek(menuId: string): Promise<WeeklyMenu> {
    const res = await api.post<{ success: boolean; data: WeeklyMenu }>(`${base}/${menuId}/duplicate`);
    return res.data!.data;
  },

  async duplicateToWeek(menuId: string, targetWeekStartDate: string): Promise<WeeklyMenu> {
    const res = await api.post<{ success: boolean; data: WeeklyMenu }>(`${base}/${menuId}/duplicate-to-week`, {
      targetWeekStartDate,
    });
    return res.data!.data;
  },

  async publish(menuId: string): Promise<WeeklyMenu> {
    const res = await api.post<{ success: boolean; data: WeeklyMenu }>(`${base}/${menuId}/publish`);
    return res.data!.data;
  },

  async getEstimatedQuantities(menuId: string): Promise<EstimatedIngredientQuantity[]> {
    const res = await api.get<{ success: boolean; data: EstimatedIngredientQuantity[] }>(
      `${base}/${menuId}/estimated-quantities`
    );
    return res.data?.data ?? [];
  },
};
